import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatCLP, formatUSD } from "../lib/format";
import { trackEvent } from "../lib/analytics";

const PERIODS = [
    { key: "monthly", label: "Mensual", hint: "Se cobra cada mes" },
    { key: "semiannual", label: "Semestral", hint: "Se cobra cada 6 meses" },
    { key: "annual", label: "Anual", hint: "Se cobra cada 12 meses" },
];

export default function SubscriptionPanel({ bundle }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const subPrices = bundle.sub_prices || {};
    const [providers, setProviders] = useState(null);
    const available = PERIODS.filter((p) => Number(subPrices?.[p.key]?.usd || 0) > 0 || Number(subPrices?.[p.key]?.clp || 0) > 0);
    const [period, setPeriod] = useState(available[0]?.key || "monthly");
    const [loading, setLoading] = useState("");

    useEffect(() => {
        api.get("/checkout/providers").then(({ data }) => setProviders(data)).catch(() => setProviders({}));
    }, []);

    const requireAuth = () => {
        if (!user) {
            toast.info("Inicia sesión para suscribirte");
            navigate("/login", { state: { from: `/bundles/${bundle.slug}` } });
            return false;
        }
        return true;
    };

    const subscribe = async (provider) => {
        if (!requireAuth()) return;
        const price = subPrices?.[period] || {};
        const value = provider === "flow" ? Number(price.clp) || 0 : Number(price.usd) || 0;
        setLoading(provider);
        trackEvent("begin_checkout", {
            currency: provider === "flow" ? "CLP" : "USD", value,
            payment_method: `${provider}_subscription`,
            items: [{ item_id: bundle.id, item_name: bundle.name, price: value, item_category: "bundle_subscription" }],
        });
        try {
            const { data } = await api.post("/checkout/subscription/create", { bundle_id: bundle.id, period, provider });
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                toast.error("No se recibió la URL de pago");
                setLoading("");
            }
        } catch (err) {
            toast.error(apiErrorMessage(err));
            setLoading("");
        }
    };

    if (bundle.status === "coming_soon") {
        return (
            <div className="panel-elevated p-6" data-testid="subscription-panel">
                <div className="label-mono mb-2">/ Próximamente</div>
                <p className="text-sm text-avx-muted">Este bundle estará disponible por suscripción muy pronto.</p>
            </div>
        );
    }

    if (available.length === 0) {
        return (
            <div className="panel-elevated p-6" data-testid="subscription-panel">
                <div className="label-mono mb-2">/ Suscripción</div>
                <p className="text-sm text-avx-muted">Aún no hay precios de suscripción configurados para este bundle.</p>
            </div>
        );
    }

    const selected = subPrices?.[period] || {};
    const hasUsd = Number(selected.usd) > 0;
    const hasClp = Number(selected.clp) > 0;
    const flowEnabled = providers ? providers.flow?.enabled : true;
    const paypalEnabled = providers ? providers.paypal?.enabled : true;

    return (
        <div className="panel-elevated p-6" data-testid="subscription-panel">
            <div className="label-mono mb-4 text-avx-blue">/ Suscríbete a este bundle</div>

            <div className="space-y-2 mb-4">
                {available.map((p) => {
                    const pr = subPrices?.[p.key] || {};
                    const active = period === p.key;
                    return (
                        <button
                            key={p.key}
                            onClick={() => setPeriod(p.key)}
                            data-testid={`sub-period-${p.key}`}
                            className={`w-full panel p-4 text-left transition-colors ${active ? "border-avx-blue bg-avx-blue/10" : "hover:border-avx-blue/40"}`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <span className={`inline-block w-3.5 h-3.5 rounded-full border-2 ${active ? "border-avx-blue bg-avx-blue" : "border-avx-muted"}`} />
                                    <div>
                                        <div className="font-heading font-bold text-sm">{p.label}</div>
                                        <div className="text-[10px] font-mono text-avx-muted">{p.hint}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {Number(pr.clp) > 0 && <div className="font-heading font-bold text-avx-blue">{formatCLP(pr.clp)}</div>}
                                    {Number(pr.usd) > 0 && <div className="text-[11px] font-mono text-avx-muted">{formatUSD(pr.usd)}</div>}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="space-y-2">
                <button
                    onClick={() => subscribe("flow")}
                    disabled={loading === "flow" || !flowEnabled || !hasClp}
                    data-testid="subscribe-flow-btn"
                    className="w-full btn-primary py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading === "flow" ? "Redirigiendo…" : hasClp ? `Suscribirme con Flow · ${formatCLP(selected.clp)}` : "Flow no disponible para este plan"}
                </button>
                <button
                    onClick={() => subscribe("paypal")}
                    disabled={loading === "paypal" || !paypalEnabled || !hasUsd}
                    data-testid="subscribe-paypal-btn"
                    className="w-full panel p-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm hover:border-avx-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading === "paypal" ? "Redirigiendo…" : hasUsd ? `Suscribirme con PayPal · ${formatUSD(selected.usd)}` : "PayPal no disponible para este plan"}
                </button>
            </div>

            <div className="mt-4 pt-4 border-t border-avx-line text-[10px] font-mono text-avx-muted leading-relaxed">
                Flow cobra en CLP (tarjeta chilena) y PayPal en USD (internacional). Puedes cancelar cuando quieras
                desde "Mis suscripciones"; conservas el acceso hasta el final del período ya pagado.
            </div>
        </div>
    );
}
